import React from 'react'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/styles'
import { useTheme } from '@material-ui/styles'
import { imageUrlRoot, imageUrls } from 'app/constants'
import { pathJoin } from 'utils/misc'
import Theme from 'core/themes/model'
import { prop } from 'ramda'
import { PreferencesState, preferencesStoreKey } from 'core/session/preferencesReducers'
import { useSelector } from 'react-redux'
import ImageWithFallback from 'core/components/image-fallback'
import Text from 'core/elements/Text'
import { RootState } from 'app/store'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import { isOpenCloud } from 'core/utils/helpers'

const useStyles = makeStyles<Theme>((theme) => ({
  page: {
    minWidth: '100vw',
    minHeight: '100vh',
    boxSizing: 'border-box',
    backgroundColor: theme?.components?.frame?.background,
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',

    '@media (max-width:992px)': {
      '& .form-page-footer': {
        maxWidth: 250,
      },
    },
  },
  container: {
    width: 560,
    minHeight: 600,
    borderRadius: 16,
    border: `solid 1px ${theme?.components?.card?.border}`,
    display: 'grid',
    overflow: 'hidden',

    '@media (max-width:1200px)': {
      width: 912,
      '& .left-pane': {
        padding: '0 58px',
      },
      '& .right-pane': {
        paddingLeft: 48,
        paddingRight: 48,
      },
    },
    '@media (max-width:992px)': {
      gridTemplateColumns: 'unset',
      maxWidth: 400,
      minHeight: 475,
      width: '100%',
      '& .left-pane': {
        display: 'none',
      },
    },
    '@media (max-width:480px)': {
      maxWidth: 360,
      '& .right-pane': {
        paddingLeft: 24,
        paddingRight: 24,
      },
    },
  },
  managementPlane: {
    backgroundColor: theme?.components?.frame?.accentBackground,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 80px',
    borderRight: `solid 1px ${theme?.components?.card?.border}`,
  },
  formPane: {
    position: 'relative',
    padding: '48px 24px',
    backgroundColor: theme?.components?.card?.background,
    display: 'grid',
    alignItems: 'center',
    justifyItems: 'center',
    gridGap: theme.spacing(2),
  },
  img: {
    maxWidth: '100%',
  },
  logo: {
    maxWidth: 280,
    maxHeight: 100,
    objectFit: 'contain',
    marginBottom: theme.spacing(6),
  },
  footer: {
    marginTop: 16,
  },
}))

const images = {
  light: {
    logoUrl: imageUrls.lightLogoPrimary,
    planeUrl: imageUrls.lightManagementPane,
  },
  dark: {
    logoUrl: imageUrls.darkLogoPrimary,
    planeUrl: imageUrls.darkManagementPane,
  },
}

const useOpenCloudStyles = makeStyles<Theme>((theme) => ({
  rackspaceLogoContainer: {
    marginBottom: theme.spacing(6),
  },
  rackspaceLogoText: {
    fontStyle: 'italic',
    color: theme.palette.grey[1000],
  },
  opencloudText: { fontWeight: 'bold', fontSize: '32px', lineHeight: '28.8px' },
  rackspaceText: {
    marginLeft: theme.spacing(6),
    fontSize: '24px',
    lineHeight: '21.79px',
  },
}))
const FormPageContainer = ({ children, className = undefined, footer = undefined }) => {
  const classes = useStyles()
  const openCloudClasses = useOpenCloudStyles()
  const theme: Theme = useTheme()
  const { loginLogoUrl, loginLogoSrc } = useSelector(
    prop<string, PreferencesState>(preferencesStoreKey),
  )
  const imageSource = loginLogoSrc ? `data:image/png;base64,${loginLogoSrc}` : ''

  const { logoUrl: defaultLogoUrl, planeUrl } = images[theme.palette.type]

  return (
    <section
      id={clsx('form-page-container', className)}
      className={clsx('form-page-container', classes.page)}
    >
      {isOpenCloud() ? (
        <div className={openCloudClasses.rackspaceLogoContainer}>
          <Text
            className={clsx(openCloudClasses.rackspaceLogoText, openCloudClasses.opencloudText)}
          >
            opencloud
          </Text>
          <Text
            className={clsx(openCloudClasses.rackspaceLogoText, openCloudClasses.rackspaceText)}
          >
            rackspace
          </Text>
        </div>
      ) : (
        <ImageWithFallback
          src={imageSource || loginLogoUrl}
          fallbackSrc={defaultLogoUrl}
          alt="PCD Logo"
          className={classes.logo}
        />
      )}
      <article className={classes.container}>
        {/* <div className={clsx('left-pane', classes.managementPlane)}>
          <img alt="PCD" src={planeUrl} className={classes.img} />
        </div> */}
        <div className={classes.formPane}>{children}</div>
      </article>
      {footer && <footer className={clsx('form-page-footer', classes.footer)}>{footer}</footer>}
    </section>
  )
}

export default FormPageContainer
